import { PublicKey } from '@solana/web3.js';
import { Program } from '@coral-xyz/anchor';
import { HabitatStakingOffchain } from '../habitat_staking_offchain';

/**
 * Check if a given public key is an admin by querying the global_config account
 */
export async function isUserAdmin(
  program: Program<HabitatStakingOffchain> | null,
  userPublicKey: PublicKey | null
): Promise<boolean> {
  if (!program || !userPublicKey) {
    return false;
  }

  try {
    // Get global config PDA
    const [globalConfig] = PublicKey.findProgramAddressSync(
      [Buffer.from('global_config')],
      program.programId
    );

    // Fetch the global config account
    const configAccount = await program.account.globalConfig.fetch(globalConfig);

    // Check if user is in the admins list
    const isAdmin = configAccount.admins.some(
      (admin: PublicKey) => admin.toString() === userPublicKey.toString()
    );

    return isAdmin;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Get list of all admins from global config
 */
export async function getAdminsList(
  program: Program<HabitatStakingOffchain> | null
): Promise<PublicKey[]> {
  if (!program) {
    return [];
  }

  try {
    const [globalConfig] = PublicKey.findProgramAddressSync(
      [Buffer.from('global_config')],
      program.programId
    );

    const configAccount = await program.account.globalConfig.fetch(globalConfig);

    // Filter out default/empty public keys
    return configAccount.admins.filter(
      (admin: PublicKey) => admin.toString() !== PublicKey.default.toString()
    );
  } catch (error) {
    console.error('Error fetching admins list:', error);
    return [];
  }
}

/**
 * Verify admin status through backend and get admin API key
 * This is more secure as it verifies the wallet signature and checks on-chain admin status
 * before returning the API key
 */
export async function verifyAdminAndGetApiKey(
  wallet: {
    publicKey: PublicKey;
    signMessage: (message: Uint8Array) => Promise<Uint8Array>;
  }
): Promise<{
  success: boolean;
  isAdmin: boolean;
  adminApiKey?: string;
  error?: string;
}> {
  try {
    const API_URL = import.meta.env.VITE_INDEXER_URL || 'https://habitat-helius-indexer.habitatsystemacc.workers.dev';
    
    // Create a message to sign with timestamp to prevent replay attacks
    const message = `Verify admin access for Habitat Staking\nWallet: ${wallet.publicKey.toString()}\nTimestamp: ${Date.now()}`;
    const messageBytes = new TextEncoder().encode(message);
    
    // Sign the message with the wallet
    const signature = await wallet.signMessage(messageBytes);
    const signatureBase64 = Buffer.from(signature).toString('base64');

    // Send verification request to backend
    const response = await fetch(`${API_URL}/admin/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        walletAddress: wallet.publicKey.toString(),
        signature: signatureBase64,
        message: message,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Verification failed' }));
      return {
        success: false,
        isAdmin: false,
        error: errorData.error || 'Verification failed',
      };
    }

    const data = await response.json();
    return {
      success: data.success,
      isAdmin: data.isAdmin,
      adminApiKey: data.adminApiKey,
    };
  } catch (error: any) {
    console.error('Error verifying admin status:', error);
    return {
      success: false,
      isAdmin: false,
      error: error.message || 'Failed to verify admin status',
    };
  }
}

